import { useCallback, useState, useEffect, Fragment } from 'react';
import cc from 'classnames';
import { observer } from 'decorators';
import { makeStyles } from 'hooks';
import { Grid, Typography, List, ListItem, ListItemText, ListItemIcon, Collapse } from '@material-ui/core';
import Input, { AddressInput, NameInput } from 'components/form/input';
import DropDownButton from 'components/dropdown';
import Modal from 'components/modal';
import Button from 'components/button';
import NavLink from 'components/navLink';
import ImageUpload from 'components/dropzone/imageUpload';
import Image from 'components/image';
import { singleLineAddress, isAddressEmpty } from 'utils/address';
import media from 'services/media';
import userService from 'services/data/user';
import { insurerTitle, insurerCardUrls, insurerCardHandleUpload, insurerCardDelete } from 'services/forms/insurer';

import { DotsVertical, Plus, Pencil, Delete } from 'mdi-material-ui';

const noop = () => {};
export default observer(function InsurerInput({ form, field, insurerIndex, copayPath, simplified, isTentative, inlineUpload, inlinePortalRef, onShowEditor: handleShowEditor, onRemove: handleRemove, onRefresh: handleRefresh, clientId, docId }) {
  handleRefresh = handleRefresh || noop;
  useEffect(handleRefresh);

  const [ isAddressOpen, setIsAddressOpen ] = useState(false);
  const [ currentAddress, setCurrentAddress ] = useState(null);

  const isSmall = media.is('xs');

  const parts = field.split('.');
  const index = Number(parts[parts.length - 1]);
  const canShowCopay = index === 0;
  const heading = insurerTitle(index);
  const showInsuredDetails = form.$(`${field}.insuredRelationship`).value !== 1;

  const address = simplified ? {} : form.$(`${field}.address`).value;
  const isNewAddress = isAddressEmpty(address);
  const addressText = isNewAddress ? 'Add Insurer Address' : 'Edit Insurer Address';
  const addressIcon = isNewAddress ? <Plus /> : <Pencil />;

  const handleEdit = useCallback(() => {
    setIsAddressOpen(true);
    setCurrentAddress(form.$(`${field}.address`).value);
  }, [ form, field ]);

  const handleClose = useCallback(() => {
    // Reset the form to what it was before
    form.$(`${field}.address`).set('value', currentAddress);
    setIsAddressOpen(false);
  }, [ form, field, currentAddress ]);

  const handleUpdate = useCallback(() => setIsAddressOpen(false), []);

  const handleClear = useCallback(() => {
    form.$(`${field}.address`).clear();
    setIsAddressOpen(false);
  }, [ form, field ]);

  const photoForm = form.$(field);
  const shouldShowSaved = !simplified && !(userService.insurerSettings.isLoading || userService.insurerSettings.value?.useOnlySaved);

  // Relative position so that the insurer photo can use absolute position to sit on top of the editor
  return <Grid container direction="column">
    <Grid item>
      <Grid container alignItems="center">
        <Grid item xs>
          <Typography variant="h6">{heading}</Typography>
          { !simplified && <Typography variant="caption">{singleLineAddress(address)}</Typography> }
        </Grid>
        { !simplified &&
          <Grid item>
            { !handleRemove &&
              <Button color="primary" onClick={handleEdit}>{addressIcon}&nbsp;&nbsp;{addressText}</Button>
            }
            { handleRemove &&
              <DropDownButton icon buttonContent={<DotsVertical />} direction="right">
                <List disablePadding>
                  <ListItem button onClick={handleEdit}>
                    <ListItemIcon>
                      {addressIcon}
                    </ListItemIcon>
                    <ListItemText primary={addressText} />
                  </ListItem>
                  { handleRemove &&
                    <ListItem button onClick={handleRemove}>
                      <ListItemIcon>
                        <Delete />
                      </ListItemIcon>
                      <ListItemText primary="Remove" />
                    </ListItem>
                  }
                </List>
              </DropDownButton>
            }
            { isAddressOpen &&
              <Modal open label="Insurer Address (Optional)" onClose={handleClose}>
                <Grid container direction="column" spacing={2}>
                  <Grid item>
                    <Typography variant="subtitle1">{heading}</Typography>
                    <Typography variant="caption">{form.$(`${field}.name`).value}</Typography>
                  </Grid>
                  <Grid item>
                    <AddressInput form={form} field={`${field}.address`} />
                  </Grid>
                  <Grid item>
                    <Grid container>
                      <Grid item xs>
                        <Button color="secondary" onClick={handleClear}>Clear Address</Button>
                      </Grid>
                      <Grid item>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button color="primary" onClick={handleUpdate}>Update</Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Modal>
            }
          </Grid>
        }
      </Grid>
    </Grid>
    <Grid item>
      <Grid container spacing={isSmall ? 0 : 2}>
        <Grid item xs={12} sm={6}>
          <InsurancePhotoInput inline={inlineUpload} inlinePortalRef={inlinePortalRef} onShowEditor={handleShowEditor} cardKey="front" form={photoForm} clientId={clientId} docId={docId} isTentative={isTentative} onRefresh={handleRefresh} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InsurancePhotoInput inline={inlineUpload} inlinePortalRef={inlinePortalRef} onShowEditor={handleShowEditor} cardKey="back" form={photoForm} clientId={clientId} docId={docId} isTentative={isTentative} onRefresh={handleRefresh} />
        </Grid>
      </Grid>
    </Grid>
    <Grid item>
      <Input form={form} field={`${field}.name`} />
    </Grid>
    { !simplified &&
      <Grid container spacing={isSmall ? 0 : 2}>
        <Grid item xs={12} sm={6}>
          <Input form={form} field={`${field}.insurerId`} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Input form={form} field={`${field}.clearing`} />
        </Grid>
      </Grid>
    }
    { !simplified &&
      <Grid item>
        <Input form={form} field={`${field}.description`} />
      </Grid>
    }
    { !simplified &&
      <Grid item>
        <Input form={form} field={`${field}.phoneNumber`} />
      </Grid>
    }
    <Grid item>
      <Grid container spacing={isSmall ? 0 : 2}>
        <Grid item xs={12} sm={6}>
          <Input form={form} field={`${field}.insuredNumber`} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Input form={form} field={`${field}.groupId`} />
        </Grid>
      </Grid>
    </Grid>
    <Grid item>
      <Grid container spacing={isSmall ? 0 : 2}>
        <Grid item xs={12} sm={6}>
          <Input form={form} field={`${field}.type`} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Input form={form} field={`${field}.insuredRelationship`} />
        </Grid>
      </Grid>
    </Grid>
    { /* Only applies to secondary medicare insurance */ }
    { !simplified && insurerIndex != null && insurerIndex > 0 && form.$(`${field}.type`).value === 1 &&
      <Grid item>
        <Input form={form} field={`${field}.medicareTypeCode`} />
      </Grid>
    }
    <Collapse in={showInsuredDetails} onEntered={handleRefresh} onExited={handleRefresh}>
      <Grid item>
        <br />
        <NameInput hideTitle form={form} field={`${field}.insuredName`} label="Insured's Details" />
        <Grid container spacing={isSmall ? 0 : 2}>
          <Grid item xs={12} sm={6}>
            <Input form={form} field={`${field}.insuredDOB`} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input form={form} field={`${field}.insuredGender`} />
          </Grid>
        </Grid>
        { !simplified && <Input form={form} field={`${field}.insuredContactNumber`} /> }
        { !simplified && <Input form={form} field={`${field}.isSameAsClient`} /> }
        { !simplified &&
          <Collapse in={!form.$(`${field}.isSameAsClient`).value}>
            <AddressInput form={form} field={`${field}.insuredAddress`} />
          </Collapse>
        }
      </Grid>
    </Collapse>
    { canShowCopay && copayPath &&
      <Grid item>
        <Input form={form} field={copayPath} />
      </Grid>
    }
    { !simplified &&
      <Grid item>
        <Input form={form} field={`${field}.additionalDetails`} />
      </Grid>
    }
    { shouldShowSaved &&
      <Grid item>
        <Input form={form} field={`${field}.shouldSave`} />
      </Grid>
    }
  </Grid>;
});

const usePhotoStyles = makeStyles(theme => ({
  imageContainer: {
    width: '100%',
    paddingBottom: '56.25%',
    position: 'relative'
  },
  deleteImage: {
    position: 'absolute',
    top: theme.spacing(-1),
    right: theme.spacing(-1)
  },
  image: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    backgroundColor: theme.palette.primary['100']
  },
  fileImage: {
    maxHeight: '100%',
    maxWidth: '100%'
  },
  noImage: {
    minWidth: theme.spacing(24),
    '&:after': {
      content: "'No Image'",
      position: 'absolute',
      top: '50%',
      left: '50%',
      fontSize: 30,
      marginLeft: -65,
      marginTop: -20
    }
  },
  disabled: {
    color: theme.palette.action.disabled,
    backgroundColor: theme.palette.action.disabledBackground,
    cursor: 'auto'
  }
}));

export const InsurancePhotoInput = observer(function InsurancePhotoInput({ form, inline, inlinePortalRef, disabled, cardKey, clientId, docId, isTentative, onShowEditor, onRefresh: handleRefresh }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleRefresh || noop);

  const classes = usePhotoStyles();

  const handleBlob = useCallback(b => insurerCardHandleUpload(form, cardKey, b, { isTentative, clientId, docId }), [ form, cardKey, isTentative, clientId, docId ]);

  const handleDelete = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    insurerCardDelete(form, cardKey, { isTentative });
  }, [ form, cardKey, isTentative ]);

  const handleShowEditor = useCallback(e => {
    onShowEditor && onShowEditor(e, cardKey);
  }, [ onShowEditor, cardKey ]);

  const canUpload = !disabled && !!(isTentative || clientId);
  const urls = insurerCardUrls(form, cardKey, { isTentative, clientId, docId, isForm: true });
  let inner = <Fragment>
    <Image className={classes.fileImage} file={urls && urls.thumbnail} fallbackComponent={<span />} alt="Insurance Card Image" />
    { !!urls && <Button className={classes.deleteImage} icon onClick={handleDelete} disabled={disabled}><Delete /></Button> }
  </Fragment>;

  if (urls) {
    inner = <NavLink className={classes.image} fileLink={urls.url}>{ inner }</NavLink>;
  } else {
    inner = <div className={cc(classes.image, !urls && classes.noImage, disabled && classes.disabled)}>{ inner }</div>;
  }

  inner = <div className={classes.imageContainer}>
    { inner }
  </div>;

  if (canUpload) {
    inner = <ImageUpload
      inline={inline}
      inlinePortalRef={inlinePortalRef}
      onShowEditor={handleShowEditor}
      disableClick={!!urls}
      cropSettings={{ keepSelection: true }}
      onImageBlob={handleBlob}
      onRefresh={handleRefresh}
    >
      { inner }
    </ImageUpload>;
  }

  const caption = cardKey === 'front' ? 'Front' : (cardKey === 'back' ? 'Back' : null);
  return <Fragment>
    { caption && <Typography variant="caption">{ caption } of Card</Typography> }
    { inner }
  </Fragment>;
});